"use client"

import { useState, useRef, useCallback, useEffect } from "react"
import type { MouseEvent } from "react"
import { useSidebarContext } from "./useSidebarContext"

/**
 * Valor de retorno del hook useSidebarIndicator
 * @interface
 */
interface UseSidebarIndicatorReturn {
  /** Indica si el indicador está visible */
  isVisible: boolean
  /** Indica si el mouse está sobre el área de items */
  isOverItems: boolean
  /** Elemento del item actualmente hovered o enfocado (para FluidHoverIndicator) */
  currentItemElement: HTMLElement | null
  /** ID del item bajo el indicador (para tooltip con subContent) */
  hoveredItemId: string | null
  /** Label del item hovered (para tooltip simple) */
  hoveredLabel: string | null
  /** Manejador de evento mousemove */
  handleMouseMove: (e: MouseEvent<HTMLDivElement>) => void
  /** Manejador de evento mouseenter */
  handleMouseEnter: () => void
  /** Manejador de evento mouseleave */
  handleMouseLeave: () => void
  /** Manejador de evento focus */
  handleFocus: () => void
  /** Manejador de evento blur */
  handleBlur: (e: React.FocusEvent) => void
  /** Función para limpiar el estado hovered desde fuera (tooltip mouseleave) */
  clearHoveredState: () => void
}

/**
 * Hook para gestionar el indicador visual del Sidebar
 *
 * @description
 * Hook que implementa la lógica del indicador visual que sigue al cursor
 * del mouse dentro del sidebar. El indicador se muestra cuando:
 * - El mouse está sobre el sidebar (isHovering = true)
 * - El cursor está en el área de items (isOverItems = true)
 *
 * Utiliza requestAnimationFrame para optimizar el rendimiento y evitar
 * cálculos excesivos durante el movimiento del mouse.
 *
 * El indicador también se actualiza cuando se navega por teclado,
 * integrándose con useSidebarKeyboard.
 *
 * @returns {UseSidebarIndicatorReturn} Estado y manejadores de eventos
 *
 * @example
 * ```tsx
 * function SidebarNav() {
 *   const {
 *     isVisible,
 *     handleMouseMove,
 *     handleMouseEnter,
 *     handleMouseLeave
 *   } = useSidebarIndicator()
 *
 *   return (
 *     <nav
 *       onMouseMove={handleMouseMove}
 *       onMouseEnter={handleMouseEnter}
 *       onMouseLeave={handleMouseLeave}
 *     >
 *       <SidebarIndicator visible={isVisible} />
 *       {children}
 *     </nav>
 *   )
 * }
 * ```
 */
export function useSidebarIndicator(): UseSidebarIndicatorReturn {
  const {
    indicatorRef,
    navRef,
    contentRef,
    showIndicatorForKeyboard,
    setShowIndicatorForKeyboard,
    isOverItems,
    setIsOverItems,
    currentItemElement,
    setCurrentItemElement,
  } = useSidebarContext()

  const [isHovering, setIsHovering] = useState(false)
  const [refsReady, setRefsReady] = useState(false)
  const [hoveredItemId, setHoveredItemId] = useState<string | null>(null)
  const [hoveredLabel, setHoveredLabel] = useState<string | null>(null)

  // Ref para almacenar el ID del requestAnimationFrame
  const rafRef = useRef<number | null>(null)

  // Verificar que todas las refs estén listas
  useEffect(() => {
    if (indicatorRef.current && navRef.current && contentRef.current) {
      setRefsReady(true)
    }
  }, [indicatorRef, navRef, contentRef, indicatorRef.current, navRef.current, contentRef.current])

  /**
   * Manejador de mousemove con optimización via requestAnimationFrame
   *
   * @param {MouseEvent<HTMLDivElement>} e - Evento de mouse
   */
  const handleMouseMove = useCallback(
    (e: MouseEvent<HTMLDivElement>) => {
      if (!indicatorRef.current || !navRef.current || !contentRef.current) {
        return
      }

      // Cancelar frame anterior si existe
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current)
      }

      // Programar actualización en el próximo frame
      rafRef.current = requestAnimationFrame(() => {
        if (!indicatorRef.current || !navRef.current || !contentRef.current) return

        const navRect = navRef.current.getBoundingClientRect()
        const contentRect = contentRef.current.getBoundingClientRect()

        // Calcular posición Y relativa al nav
        const y = e.clientY - navRect.top

        // Obtener límites del área de contenido
        const minY = contentRect.top - navRect.top
        const maxY = contentRect.bottom - navRect.top

        // Clamp Y dentro del área de contenido
        const clampedY = Math.max(minY, Math.min(maxY, y))

        // Actualizar posición del indicador
        indicatorRef.current.style.top = `${clampedY}px`

        // Determinar si el cursor está en el área de items
        const isInItemsArea = e.clientY >= contentRect.top && e.clientY <= contentRect.bottom

        setIsOverItems(isInItemsArea)

        // Detectar el item actualmente hovered (para FluidHoverIndicator y tooltip)
        const hoveredElement = document.elementFromPoint(e.clientX, e.clientY)

        // Buscar el item con data-sidebar-item (puede estar en un padre si el cursor está sobre un hijo)
        const itemElement = hoveredElement?.closest('[data-sidebar-item]') as HTMLElement | null

        if (itemElement) {
          setCurrentItemElement(itemElement)

          // Extraer label para tooltip simple (items sin subContent)
          const label = itemElement.getAttribute('aria-label') || itemElement.getAttribute('title')
          setHoveredLabel(label || null)

          // Extraer itemId para tooltip con subContent
          const itemId = itemElement.getAttribute("data-item-id")
          setHoveredItemId(itemId || null)
        } else {
          // Si no hay item hovered, limpiar
          setCurrentItemElement(null)
          setHoveredItemId(null)
          setHoveredLabel(null)
        }
      })
    },
    [indicatorRef, navRef, contentRef],
  )

  /**
   * Manejador de mouseenter
   */
  const handleMouseEnter = useCallback(() => {
    setIsHovering(true)
  }, [])

  /**
   * Manejador de mouseleave
   */
  const handleMouseLeave = useCallback(() => {
    setIsHovering(false)
    setIsOverItems(false)
    setShowIndicatorForKeyboard(false)
    setCurrentItemElement(null)
    setHoveredLabel(null)

    // Cancelar animación pendiente
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current)
    }
  }, [setShowIndicatorForKeyboard, setIsOverItems])

  /**
   * Manejador de focus (cuando se enfoca un elemento dentro del sidebar)
   */
  const handleFocus = useCallback(() => {
    setIsHovering(true)
    setIsOverItems(true)
  }, [setIsOverItems])

  /**
   * Manejador de blur (cuando se pierde el focus)
   *
   * @param {React.FocusEvent} e - Evento de blur
   */
  const handleBlur = useCallback(
    (e: React.FocusEvent) => {
      // Solo ocultar si el focus sale completamente del sidebar
      if (!navRef.current?.contains(e.relatedTarget as Node)) {
        setIsHovering(false)
        setIsOverItems(false)
        setShowIndicatorForKeyboard(false)
      }
    },
    [navRef, setShowIndicatorForKeyboard, setIsOverItems],
  )

  /**
   * Limpia el estado hovered (para cuando el mouse sale del tooltip)
   */
  const clearHoveredState = useCallback(() => {
    setHoveredLabel(null)
    setHoveredItemId(null)
    setCurrentItemElement(null)
  }, [])

  // El indicador es visible cuando:
  // - Mouse hover: refsReady && isHovering && isOverItems
  // - Keyboard focus: refsReady && showIndicatorForKeyboard
  const isVisible = refsReady && ((isHovering && isOverItems) || showIndicatorForKeyboard)

  return {
    isVisible,
    isOverItems,
    currentItemElement,
    hoveredItemId,
    hoveredLabel,
    handleMouseMove,
    handleMouseEnter,
    handleMouseLeave,
    handleFocus,
    handleBlur,
    clearHoveredState,
  }
}
